import { motion } from 'framer-motion'
import SEO from '../components/SEO'
import Footer from '../components/Footer'

const sections = [
  {
    title: 'Information We Collect',
    content: 'Solarch is self-hosted software — we do not collect, store, or process any data on our servers. When you self-host Solarch, all data (including user accounts, records, files, and logs) stays entirely on your own infrastructure.\n\nIf you visit the documentation site or landing page, we may collect standard web analytics data (page views, browser type, referring site) to improve the experience.',
  },
  {
    title: 'How We Use Information',
    content: 'Any analytics data collected is used solely to understand usage patterns and improve the project. We do not sell, rent, or share personal information with third parties.',
  },
  {
    title: 'Data Storage & Security',
    content: 'When self-hosted, you are responsible for securing your own deployment. Solarch includes built-in security features — encryption at rest (SQLite), JWT-based authentication, rate limiting, and CORS controls — to help you protect your data.',
  },
  {
    title: 'Cookies',
    content: 'The documentation site may use essential cookies for functionality (e.g., theme preferences). No tracking cookies are used.',
  },
  {
    title: 'Third-Party Services',
    content: 'Solarch integrates with optional third-party services you explicitly configure: S3-compatible storage, SMTP email providers, and AI providers (OpenAI, Anthropic, Ollama). Data sent through these services is governed by their respective privacy policies.',
  },
  {
    title: 'Open Source',
    content: 'Solarch is open source under the Apache-2.0 license. You can audit the code at any time on GitHub to verify our privacy commitments.',
  },
  {
    title: 'Contact',
    content: 'If you have questions about this privacy policy, reach out via GitHub Discussions or open an issue on the repository.',
  },
]

export default function Privacy() {
  return (
    <>
      <SEO 
        title="Privacy Policy — Solarch"
        description="Privacy Policy for Solarch — your data stays on your infrastructure."
        canonical="/privacy"
      />

      <div className="min-h-screen bg-theme-body pt-14">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute right-1/4 top-0 h-[400px] w-[400px] rounded-full bg-primary/5 blur-[120px]" />
          </div>

          <div className="relative mx-auto max-w-3xl px-4 pt-20 pb-8 sm:px-6 lg:px-8 lg:pt-28 lg:pb-12 text-center">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
            >
              <h1 className="font-heading text-4xl font-bold tracking-tight text-[#fdf3ec] sm:text-5xl">
                Privacy Policy
              </h1>
              <p className="mx-auto mt-4 text-base text-[#8b6d5b]">
                Last updated: May 1, 2026
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-6 lg:px-8">
          <div className="space-y-0">
            {sections.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ y: 16, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.04, ease: [0.32, 0.72, 0, 1] }}
                className="relative py-8 border-b border-[#3a2214]/50 last:border-b-0 group"
              >
                <div className="flex items-start gap-5">
                  <span className="shrink-0 mt-1 font-mono text-sm text-primary/60 tabular-nums w-6 text-right select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading text-lg font-bold text-[#fdf3ec] mb-3 tracking-tight">
                      {s.title}
                    </h2>
                    {s.content.split('\n\n').map((p, j) => (
                      <p key={j} className="text-[15px] text-[#c9a894] leading-[1.7] mb-3 last:mb-0 max-w-[65ch]">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Footer />
        </div>
      </div>
    </>
  )
}
